import React, { useEffect, useMemo } from "react";
import useIsDesktop from "../../../hooks/useIsDesktop";
import useTimedChanges from "../../../hooks/useTimedChanges";
import { PlanBox } from "./PlanBox";
import withPlans from "../../../../../../hocs/withPlans";
import { compose } from "redux";
import withTranslations from "../../../../../../hocs/withTranslations";
import {
  getAvailablePeriods,
  getCurrencyOfferByPeriod,
  getDefaultPeriod,
  isPeriodAllowed
} from "../../../../../../lib/Plans";
import FormatCurrency from "./FormatCurrency";
import Period from "../../Plans/components/Period";
import { getPaymentMethodPricing } from "../../Plans/utils/useOnPaymentButtonClick";
import { PlanTableBoxContent } from "./PlanTableBoxContent";
import { PlanTableCellFixedHeight } from "./PlanTableCellFixedHeight";
import Loader from "../../../../common/Loader";
import PlanOptions from "./PlanOptions";
import { getAvailablePlanNames } from "../../Plans/utils/getAvailablePlanNames";

const PlanTable = ({ plan, setPlan, translate, offers, locale }) => {
  const isDesktop = useIsDesktop();
  const { ToggleSelectPlan } = useTimedChanges({
    translate
  });

  const currentOffer = offers?.data?.offers?.[0];
  const allowedPeriods = useMemo(
    () => getAvailablePeriods(currentOffer),
    [currentOffer]
  );
  const currentPeriod = typeof plan === "number" ? plan : 0;

  useEffect(() => {
    if (!allowedPeriods.length) {
      return;
    }

    setPlan(current =>
      isPeriodAllowed(current, allowedPeriods)
        ? current
        : getDefaultPeriod(allowedPeriods) ?? current
    );
  }, [allowedPeriods, setPlan]);

  if (offers.loading !== false) {
    return (
      <div>
        <Loader />
      </div>
    );
  }

  const toggleSelectPlan = (
    <ToggleSelectPlan
      plan={plan}
      setPlan={setPlan}
      allowedPeriods={allowedPeriods}
    />
  );

  if (currentOffer?.plans?.length <= 0) {
    return null;
  }

  if (isDesktop)
    return (
      <div className={"plan-table"}>
        <table className={"plan-table__table"}>
          <thead>
            <tr>
              <th className={"align-top"}>
                <div className={"flex gap-8 items-center"}>
                  <label className={"text-2xl.1 font-semibold mb-0"}>
                    {translate("lp_plan-table_toggle-select_desktop-label")}
                  </label>
                  {toggleSelectPlan}
                </div>
              </th>
              {currentOffer.plans.map(plan => {
                const currencyOffer = getCurrencyOfferByPeriod({
                  currency_offers: plan.currency_offers,
                  period: currentPeriod
                });

                return currencyOffer ? (
                  <PlanTableCellFixedHeight key={plan.id}>
                    <PlanTableBoxContent
                      translate={translate}
                      plan={plan}
                      currentPeriod={currentPeriod}
                      locale={locale}
                    />
                  </PlanTableCellFixedHeight>
                ) : null;
              })}
            </tr>
          </thead>
          <tbody>
            <PlanOptions
              count={20}
              availablePlans={getAvailablePlanNames({
                currentOffer,
                currentPeriod
              })}
            />
          </tbody>
        </table>
      </div>
    );

  return (
    <>
      <div className={"flex gap-4 items-center mb-8"}>
        <label className={"text-xs font-semibold mb-0"}>
          {translate("lp_plan-table_toggle-select_mobile-label")}
        </label>
        {toggleSelectPlan}
      </div>
      <div className={"plan-table mb-6"}>
        <table className={"plan-table__table"}>
          <thead>
            <tr>
              <th className={"text-xs text-black-1 w-2/4"}>
                {translate("lp_plan-table_mobile-column-1_heading")}
              </th>
              {currentOffer.plans.map(plan => {
                const currencyOffer = getCurrencyOfferByPeriod({
                  currency_offers: plan.currency_offers,
                  period: currentPeriod
                });
                const paymentMethodPricing = getPaymentMethodPricing({
                  currentCurrencyOfferByPeriod: currencyOffer
                });

                return currencyOffer ? (
                  <th
                    className={"text-center text-base font-medium"}
                    key={plan.id}
                  >
                    {plan.name}
                    <br />
                    <strong className={"font-semibold"}>
                      {currencyOffer ? (
                        <div className="gap-3 flex justify-center">
                          <div className={"flex gap-1.5 align-baseline"}>
                            <strong className={"font-bold text-black"}>
                              <FormatCurrency
                                value={
                                  paymentMethodPricing?.order_monthly_amount ??
                                  0
                                }
                                locale={locale}
                              />
                            </strong>
                            <span
                              className={
                                "text-xs text-black opacity-65 whitespace-nowrap"
                              }
                            >
                              <Period periods={1} />
                            </span>
                          </div>
                        </div>
                      ) : null}
                    </strong>
                    {currencyOffer?.omnibus_price ? (
                      <div className={"ml-auto my-2"}>
                        <span
                          className={
                            "text-xs.1 text-black opacity-50 flex justify-end font-normal text-right"
                          }
                        >
                          {translate("plans_box_omnibus-price", {
                            value: FormatCurrency({
                              value: currencyOffer.omnibus_price,
                              locale
                            })
                          })}
                        </span>
                      </div>
                    ) : null}
                  </th>
                ) : null;
              })}
            </tr>
          </thead>
          <tbody>
            <PlanOptions
              count={20}
              availablePlans={getAvailablePlanNames({
                currentOffer,
                currentPeriod
              })}
            />
          </tbody>
        </table>
      </div>
      {currentOffer.plans.map(plan => {
        return (
          <PlanBox className={"mb-4"} mode={"light"} key={plan.id}>
            <PlanTableBoxContent
              translate={translate}
              plan={plan}
              currentPeriod={currentPeriod}
              locale={locale}
            />
          </PlanBox>
        );
      })}
    </>
  );
};

export default compose(withPlans, withTranslations)(PlanTable);
